import { useEffect, useState } from "react";
import { Calendar, MapPin, User, Users } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const STATUS_STYLES = {
  new: "bg-cyan-50 text-cyan-700 border-cyan-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

export const InquiriesManager = ({ onChanged }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/admin/inquiries").then((res) => { setInquiries(res.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const setStatus = async (inq, status) => {
    try {
      await api.patch(`/admin/inquiries/${inq.id}`, { status });
      load(); onChanged?.();
      toast.success(`Marked as ${status}`);
    } catch (err) { toast.error("Failed to update status"); }
  };

  return (
    <div data-testid="inquiries-manager">
      <h2 className="text-2xl font-semibold text-slate-900 mb-5">Delivery bookings</h2>
      {loaded && inquiries.length === 0 && (
        <p className="text-slate-500 text-sm" data-testid="inquiries-empty">No bookings yet. They'll appear here when customers submit the "Book a Van & Crew" form.</p>
      )}
      <div className="space-y-4">
        {inquiries.map((inq, i) => (
          <div key={inq.id} data-testid={`admin-inquiry-row-${i}`} className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <User size={16} className="text-blue-600" /> {inq.company_name}
                  </h3>
                  <Badge className={`border ${STATUS_STYLES[inq.status] || STATUS_STYLES.new} hover:bg-transparent`}>{inq.status}</Badge>
                </div>
                <p className="text-sm text-slate-600 mt-2 font-medium">{inq.service_type} <span className="text-slate-400">·</span> <Users size={13} className="inline mb-0.5" /> {inq.crew_size}{inq.preferred_date ? <> <span className="text-slate-400">·</span> <Calendar size={13} className="inline mb-0.5" /> {inq.preferred_date}</> : null}</p>
                {(inq.collection_location || inq.delivery_location) && (
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1"><MapPin size={13} /> {inq.collection_location || "—"} → {inq.delivery_location || "—"}</p>
                )}
                <div className="text-sm text-slate-500 mt-2 flex flex-wrap gap-x-5 gap-y-1">
                  {inq.contact_name && <span>{inq.contact_name}</span>}
                  <a href={`mailto:${inq.email}`} className="hover:text-blue-700 transition-colors">{inq.email}</a>
                  {inq.phone && <a href={`tel:${inq.phone}`} className="hover:text-blue-700 transition-colors">{inq.phone}</a>}
                </div>
                {inq.message && <p className="text-sm text-slate-500 mt-3 bg-slate-50 rounded-md p-3">{inq.message}</p>}
                <p className="text-xs text-slate-400 mt-3">{new Date(inq.created_at).toLocaleString("en-GB")}</p>
              </div>
              <div className="shrink-0">
                <Select value={inq.status} onValueChange={(v) => setStatus(inq, v)}>
                  <SelectTrigger className="w-36" data-testid={`admin-inquiry-status-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

